"use client";

import { Button } from "@/components/ui/button";
import { EmptyField } from "@/components/ui/empty-field";
import { Separator } from "@/components/ui/separator";
import { RegistrationData } from "@/types/RegistrationData";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import PersonalInfoForm, { PersonalFormValues } from "./personal-info-form";
import { TabDadosSkeleton } from "./tab-data-skeleton";

type PersonalInfo = PersonalFormValues["personalInfo"];

interface TabDadosProps {
  registrationData: RegistrationData | null;
}

export default function TabDados({ registrationData }: TabDadosProps) {
  const formMethods = useForm<PersonalFormValues>({
    defaultValues: {
      personalInfo: {
        cli_razao_social: "",
        cli_celular: "",
        cli_email: "",
        cli_cnpj: "",
        cli_uf: "",
        cli_nacionalidade: "",
        cli_genero: "",
        cli_rg: "",
        cli_rg_validade: "",
      },
    },
  });

  const [info, setInfo] = useState<PersonalInfo>({
    cli_razao_social: "",
    cli_celular: "",
    cli_email: "",
    cli_cnpj: "",
    cli_uf: "",
    cli_nacionalidade: "",
    cli_genero: "",
    cli_rg: "",
    cli_rg_validade: "",
  });

  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!registrationData) return;

    const p = registrationData.pessoais?.[0];
    const end = registrationData.enderecos?.[0];
    const values: PersonalInfo = {
      cli_razao_social: p?.cli_razao_social ?? "",
      cli_celular: p?.cli_celular ?? "",
      cli_email: p?.cli_email ?? "",
      cli_cnpj: p?.cli_cnpj ?? "",
      cli_uf: end?.municipio ? (end.municipio.split("-").pop() ?? "") : (p?.cli_uf ?? ""),
      cli_nacionalidade: p?.cli_nacionalidade ?? "",
      cli_genero: p?.cli_genero ?? "",
      cli_rg: p?.cli_rg ?? "",
      cli_rg_validade: p?.cli_rg_validade ?? "",
    };
    setInfo(values);
    formMethods.reset({ personalInfo: values });
  }, [registrationData, formMethods]);

  const handleSaved = () => {
    const values = formMethods.getValues("personalInfo");
    setInfo(values);
    setEditing(false);
  };

  const handleCancel = () => setEditing(false);

  if (!registrationData) {
    return <TabDadosSkeleton />;
  }

  const fields: Array<[string, string]> = [
    ["Nome", info.cli_razao_social],
    ["Telefone", info.cli_celular],
    ["Email", info.cli_email],
    ["CPF/CNPJ", info.cli_cnpj],
    ["Código do cliente", registrationData.pessoais?.[0]?.cli_codigo || ""],
  ];

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-base sm:text-lg font-semibold text-n-primary">
          Meus dados pessoais:
        </h2>
      </div>

      <Separator />

      {editing && (
        <FormProvider {...formMethods}>
          <PersonalInfoForm onSaved={handleSaved} onCancel={handleCancel} />
        </FormProvider>
      )}

      {!editing && (
        <>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {fields.map(([label, value]) => (
              <div key={label} className="rounded-md p-2">
                <dt className="text-xs sm:text-sm font-semibold text-n-text-primary">
                  {label}:
                </dt>
                <dd className="text-sm sm:text-base text-n-text-neutral break-words">
                  {value?.trim() ? (
                    value
                  ) : (
                    <EmptyField>Não informado</EmptyField>
                  )}
                </dd>
              </div>
            ))}
          </dl>

          <div className="flex justify-start">
            <Button
              variant="link"
              size="sm"
              type="button"
              className="px-2"
              onClick={() => setEditing(true)}
            >
              Editar
              <ArrowRight size={14} />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
